import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { customerService } from '../services/customerService';
import CurrencyTextField, { formatCurrency } from './CurrencyTextField';

const ApplyCustomerCreditDialog = ({
  open,
  onClose,
  onSuccess,
  customerId,
  invoiceId,
  balanceDue,
  prepaidBalance
}) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const available = Math.max(0, parseFloat(prepaidBalance) || 0);
  const maxApply = Math.min(available, parseFloat(balanceDue) || 0);

  useEffect(() => {
    if (open) {
      setError('');
      setAmount(maxApply > 0 ? String(Math.round(maxApply * 100) / 100) : '');
    }
  }, [open, maxApply]);

  const handleApply = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError('Enter a positive amount.');
      return;
    }
    if (amt > maxApply + 0.01) {
      setError(`Cannot apply more than ${formatCurrency(maxApply)}.`);
      return;
    }
    try {
      setSaving(true);
      setError('');
      await customerService.applyPrepaidCredit(customerId, {
        invoice_id: invoiceId,
        amount: amt
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to apply customer credit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Apply customer credit</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Applies unapplied funds from this customer&apos;s payments (oldest first). Credit available:{' '}
          {formatCurrency(available)} — Invoice balance: {formatCurrency(balanceDue)}
        </Typography>
        <CurrencyTextField
          fullWidth
          size="small"
          label="Amount to apply"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          minimumFractionDigits={2}
          maximumFractionDigits={2}
          dollarAdornment
          inputProps={{ min: 0, max: maxApply, step: '0.01' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={saving || maxApply <= 0}>
          {saving ? <CircularProgress size={20} /> : 'Apply credit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyCustomerCreditDialog;
